import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';

import i18n from '@nextroll/ar-i18n';
import SearchSelector, { SearchOption } from '@nextroll/ar-search-selector';
import { NodeDropDownSectionValue, NodeDropdownDefaultProps } from 'types/rofl';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';


interface IProps extends NodeDropdownDefaultProps<NodeDropDownSectionValue> {
    onRefresh?: (searchParam?: string) => void;
}

export const NodePanelSearchableDropdown = (props: IProps) => {
    const { data, loading, warning, error, renderSelected } = props;
    const [searchValue, setSearchValue] = React.useState('');
    const [selectedValue, setSelectedValue] = React.useState<NodeDropDownSectionValue | undefined> (undefined);

    // sort the dropdown options before rendering
    const sortedData = useMemo(
        () => (data || []).sort((a, b) => (a.display_value || '').localeCompare(b.display_value || '')),
        [data],
    );

    // go through dropdown options for the first time and detect the selected option
    useEffect(() => {
        const selectedOption = sortedData.find(option => renderSelected?.(option));
        setSelectedValue(selectedOption);
    }, [sortedData, renderSelected]);

    const handleSearch = useDebouncedCallback(async (search: string) => {
        props.onRefresh?.(search);
    }, 1000);

    const setSearch = (search: string) => {
        setSearchValue(search);
        handleSearch(search);
    };

    const onSelect = (search: string) => {
        const selectedOption = sortedData.find(option => option.value === search);
        props.onSelect?.({ value: search, display_value: selectedOption?.display_value || '' });
        setSelectedValue(selectedOption);
    };

    return (
        <>
            <div className={classNames('workflow-node-panel-select-dropdown', {
                warning: warning && !error,
                error,
            })}
            >
                <SearchSelector
                    i18n={i18n}
                    getDropdownLabel={() => (selectedValue?.display_value || props.placeholder)}
                    handleClearSearchValue={() => {}}
                    handleSearch={setSearch}
                    handleSelectValue={onSelect}
                    handleRemoveValue={() => setSelectedValue(undefined)}
                    handleOnClick={props.onRefresh}
                    options={sortedData.map(option => ({
                        category: '',
                        eid: option.value,
                        name: option.display_value,
                    } as SearchOption))}
                    searchValue={searchValue}
                    // set selected value as a empty set to bypass it's multiple selection layout behaviour.
                    selectedValues={new Set()}
                    renderAsSelectDropdown
                    isFetchingOptions={loading}
                    singleSelect
                    willFetch
                />

                {/* reload button and loading spinner */}
                {props.allowRefresh && props.onRefresh && (loading
                    ? <span data-testid='workflow-node-panel-loading-spinner' className='loading-spinner-small' />
                    : (
                        <i
                            className='ar-icon ar-icon-fw fa-sync-alt'
                            onClick={() => props.onRefresh?.(searchValue)}
                        />
                    ))}
            </div>

            {/* warning message if needed */}
            {warning && (
                <span className='workflow-node-panel-select-dropdown-warning-msg'>
                    {warning}
                </span>
            )}

            {/* error message if needed */}
            {props.errorElement}
        </>
    );
};
